import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IntegratorSelectOption } from './integrator-select.component';

/**
 * Multi-value sibling of {@link IntegratorSelectComponent} — same V8 design
 * language, same {label, value} option contract, but the value is an array
 * and clicking an option toggles its membership instead of closing the panel.
 *
 * <p>Kept as a separate component (rather than a {@code multi} flag on the
 * single-select) because the rendered UX diverges enough that branching every
 * other line would muddy both. Single-select shows the chosen label in the
 * trigger; multi-select shows a count + chevron, with the option list
 * staying open across clicks until the user dismisses it.
 *
 * <p>Closes on outside-click and {@code Escape}. Auto-focuses the optional
 * filter input on open.
 */
@Component({
  selector: 'lib-integrator-multi-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntegratorMultiSelectComponent),
      multi: true,
    },
  ],
  styles: [
    `
      /* See IntegratorSelectComponent for the rationale. */
      :host {
        display: block;
      }
    `,
  ],
  template: `
    <div #root class="relative" [style.width]="width()">
      <button
        type="button"
        class="flex w-full items-center gap-2 rounded-md border bg-white px-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
        [class.h-8]="size() === 'sm'"
        [class.h-9]="size() === 'md'"
        [class.border-gray-300]="!open()"
        [class.dark:border-slate-600]="!open()"
        [class]="open() ? focusBorderClass() : ''"
        [disabled]="disabled()"
        [attr.aria-haspopup]="'listbox'"
        [attr.aria-expanded]="open()"
        (click)="toggle()">
        <span
          class="min-w-0 flex-1 truncate text-left"
          [class.text-gray-400]="value().length === 0"
          [class.dark:text-slate-500]="value().length === 0">
          {{ triggerLabel() }}
        </span>
        @if (value().length > 0 && !disabled()) {
          <!-- Clear-all hit area — stops propagation so it doesn't toggle the
               panel. Sized large enough to click without missing on phones. -->
          <button
            type="button"
            class="-m-1 rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-slate-200"
            (click)="onClearAll($event)"
            title="Clear all">
            <svg class="size-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        }
        <svg
          class="size-3 shrink-0 transition-transform"
          [class.rotate-180]="open()"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      @if (open()) {
        <div
          class="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
          [class.bottom-full]="placement() === 'top'"
          [class.mb-1]="placement() === 'top'"
          [class.mt-0]="placement() === 'top'"
          role="listbox"
          [attr.aria-multiselectable]="true"
          (click)="$event.stopPropagation()">
          @if (filterable()) {
            <div class="border-b border-gray-200 p-1.5 dark:border-slate-700">
              <input
                #filterInput
                type="text"
                placeholder="Search…"
                class="focus:border-primary-500 h-7 w-full rounded border border-gray-200 bg-white px-2 text-sm text-gray-900 outline-none dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                [ngModel]="filter()"
                (ngModelChange)="filter.set($event)" />
            </div>
          }
          <ul class="max-h-60 overflow-y-auto py-1">
            @for (opt of filteredOptions(); track opt.value) {
              <li
                role="option"
                [attr.aria-selected]="isSelected(opt)"
                class="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12.5px] text-gray-700 transition-colors hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
                [class.cursor-not-allowed]="opt.disabled"
                [class.opacity-50]="opt.disabled"
                (click)="onToggle(opt)">
                <!-- Custom checkbox — Tailwind-only so we don't pull in
                     PrimeNG. Filled accent on selected, hollow border
                     otherwise. -->
                <span
                  class="flex size-4 shrink-0 items-center justify-center rounded border transition-colors"
                  [class.bg-white]="!isSelected(opt)"
                  [class.dark:bg-slate-900]="!isSelected(opt)"
                  [class.border-gray-300]="!isSelected(opt)"
                  [class.dark:border-slate-500]="!isSelected(opt)"
                  [class]="isSelected(opt) ? checkboxFillClass() : ''">
                  @if (isSelected(opt)) {
                    <svg
                      class="size-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      stroke-width="3">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  }
                </span>
                <span class="flex-1 truncate">{{ opt.label }}</span>
              </li>
            } @empty {
              <li class="px-3 py-3 text-center text-sm text-gray-500 dark:text-slate-400">No match.</li>
            }
          </ul>
        </div>
      }
    </div>
  `,
})
export class IntegratorMultiSelectComponent<T = unknown> implements ControlValueAccessor {
  options = input.required<IntegratorSelectOption<T>[]>();
  placeholder = input<string>('Select…');
  /** Word used in the trigger when items are selected, e.g. "2 sources" — let
   * the host pick the noun so the label reads naturally. Default "items". */
  itemNoun = input<string>('items');
  filterable = input<boolean>(false);
  width = input<string>('14rem');
  accent = input<'primary' | 'warmYellow' | 'neutral'>('primary');
  size = input<'sm' | 'md'>('sm');
  disabled = input<boolean>(false);

  protected readonly open = signal(false);
  protected readonly filter = signal('');
  protected readonly value = signal<T[]>([]);
  protected readonly placement = signal<'bottom' | 'top'>('bottom');

  protected readonly root = viewChild<ElementRef<HTMLDivElement>>('root');
  protected readonly filterInput = viewChild<ElementRef<HTMLInputElement>>('filterInput');

  protected readonly filteredOptions = computed<IntegratorSelectOption<T>[]>(() => {
    const f = this.filter().toLowerCase().trim();
    const list = this.options() ?? [];
    if (!f) return list;
    return list.filter(o => o.label.toLowerCase().includes(f));
  });

  /** Compose the trigger label from the current selection: empty → placeholder,
   * single → the option's label, many → "{n} {itemNoun}". */
  protected readonly triggerLabel = computed<string>(() => {
    const v = this.value();
    if (v.length === 0) return this.placeholder();
    if (v.length === 1) {
      return this.options()?.find(o => o.value === v[0])?.label ?? `1 ${this.itemNoun()}`;
    }
    return `${v.length} ${this.itemNoun()}`;
  });

  protected readonly focusBorderClass = computed<string>(() => {
    switch (this.accent()) {
      case 'warmYellow':
        return 'border-warmYellow-500 ring-1 ring-warmYellow-500';
      case 'neutral':
        return 'border-gray-400 ring-1 ring-gray-400 dark:border-slate-400 dark:ring-slate-400';
      default:
        return 'border-primary-500 ring-1 ring-primary-500';
    }
  });

  protected readonly checkboxFillClass = computed<string>(() => {
    switch (this.accent()) {
      case 'warmYellow':
        return 'bg-warmYellow-500 border-warmYellow-500';
      case 'neutral':
        return 'bg-gray-700 border-gray-700 dark:bg-slate-300 dark:border-slate-300';
      default:
        return 'bg-primary-500 border-primary-500';
    }
  });

  // ── ControlValueAccessor

  private onChange: (v: T[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(v: T[] | null): void {
    this.value.set(v ?? []);
  }

  registerOnChange(fn: (v: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    // Same note as IntegratorSelectComponent — input drives disabled state;
    // FormControl-driven disable would need a signal pipe.
  }

  // ── Open/close

  protected toggle(): void {
    if (this.disabled()) return;
    if (this.open()) this.close();
    else this.openPanel();
  }

  private openPanel(): void {
    this.open.set(true);
    this.filter.set('');

    const el = this.root()?.nativeElement;
    if (el) {
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      this.placement.set(spaceBelow < 260 && rect.top > 260 ? 'top' : 'bottom');
    }

    if (this.filterable()) {
      queueMicrotask(() => this.filterInput()?.nativeElement.focus());
    }
  }

  private close(): void {
    this.open.set(false);
    this.filter.set('');
    this.onTouched();
  }

  protected isSelected(opt: IntegratorSelectOption<T>): boolean {
    return this.value().includes(opt.value);
  }

  /** Toggle membership without closing the panel — the operator typically
   * picks several options in a row, and reopening the dropdown each time
   * would be infuriating. */
  protected onToggle(opt: IntegratorSelectOption<T>): void {
    if (opt.disabled) return;
    const current = this.value();
    const next = current.includes(opt.value) ? current.filter(v => v !== opt.value) : [...current, opt.value];
    this.value.set(next);
    this.onChange(next);
  }

  protected onClearAll(e: Event): void {
    e.stopPropagation();
    if (this.disabled()) return;
    this.value.set([]);
    this.onChange([]);
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (!this.open()) return;
    const el = this.root()?.nativeElement;
    if (el && !el.contains(e.target as Node)) this.close();
  }

  @HostListener('document:keydown.escape')
  onDocEscape(): void {
    if (this.open()) this.close();
  }
}
